import {
  AccountRecoveryErrorType,
  type AccountRecoveryOutput,
  type AccountRecoveryOutputV2,
  useAccountRecovery,
  useAccountRecoveryV2,
} from '@egw/api'
import { type GQLError } from '@egw/clients'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { useRouter } from '@egw/router'
import { CheckCircleIcon } from '@heroicons/react/20/solid'
import { type FC, useEffect, useState } from 'react'
import { useFlags } from '@egw/feature-flags'
import { Layout, PageHeader } from '../../../components'
import { type RecoveryFlow } from '../../../models'
import { getRecoveryFlowData } from '../../../services'

const log = new Logger('Passkey Recovery - Magic Link')

export const RecoveryMagicLinkPage: FC = () => {
  const [flowData, setFlowData] = useState<RecoveryFlow | null>(null)
  const [isOtpResent, setIsOtpResent] = useState(false)
  const { flags } = useFlags<FeatureFlags>()

  const { t } = useTranslation('features-auth')
  const { navigate } = useRouter()

  const handleSuccess = (res: AccountRecoveryOutput | AccountRecoveryOutputV2): void => {
    if (res.__typename === 'AccountRecoveryError' && res.errorType === AccountRecoveryErrorType.EXPIRED) {
      navigate(`${WEALTH_ROUTES.SIGN_IN.path}?recoveryExpired=true`)
      return
    }
    // eslint-disable-next-line testing-library/no-debugging-utils
    log.debug('Resent magic link')
    setIsOtpResent(true)
  }

  const handleError = (e: GQLError): void => {
    log.error('Failed to resend magic link: ', e)
  }

  const v2Result = useAccountRecoveryV2({
    onSuccess: handleSuccess,
    onError: handleError,
  })
  const v1Result = useAccountRecovery({
    onSuccess: handleSuccess,
    onError: handleError,
  })

  const { mutate, isLoading, error } = flags.rolloutIntegratedDocumentVerification ? v2Result : v1Result

  useEffect(() => {
    const d = getRecoveryFlowData()
    if (d?.email) {
      setFlowData(d)
    } else {
      navigate(WEALTH_ROUTES.RECOVERY.path)
    }
  }, [navigate])

  // TODO: redirect to DocV on successful otp verification - check if needed
  // useEffect(() => {
  //   if (data?.__typename === 'AccountRecoveryResponse') {
  //     // eslint-disable-next-line testing-library/no-debugging-utils
  //     log.debug('OTP verification successful. Redirecting to DocV')
  //     const { documentVerificationToken = null } = data
  //     // redirect to DocV for ID verification
  //     if (documentVerificationToken) {
  //       // setIsVerified(true)
  //     }
  //   }
  // }, [isSuccess])

  // Hide resent message after 5s
  useEffect(() => {
    if (isOtpResent) {
      setTimeout(() => {
        setIsOtpResent(false)
      }, 5000)
    }
  }, [isOtpResent])

  return (
    <Layout>
      <PageHeader title={t('recovery.title')} backLinkUrl={WEALTH_ROUTES.RECOVERY_MAGIC_LINK.backPath} />
      {flowData ? (
        <div className="text-body">
          <p>{t('recovery.link_emailed')}</p>
          <p className="font-bold">{flowData.email}</p>
          <p className="mt-8">{t('recovery.check_your_email')} </p>
          <div className="align-center my-6 flex flex-row">
            <button
              id="magic-link-resend_link_btn"
              data-testid="magic-link-resend_link_btn"
              type="button"
              disabled={isLoading || isOtpResent}
              className="flex items-center justify-center text-btnLinkText hover:text-btnLinkTextHover active:text-btnLinkTextActive disabled:text-btnLinkTextDisabled hover:underline font-semibold border-transparent mr-4 text-sm md:w-fit h-10 cursor-pointer"
              onClick={() => {
                sendEvent('user_reSendVerification_click', {
                  eventSource: 'egWealth',
                  category: 'passkey-recovery',
                  subCategory: 'verification',
                  verificationType: 'email',
                })
                void mutate({
                  sendVerificationMethod: true,
                })
              }}
            >
              {t('recovery.resend_link')}
            </button>

            {isOtpResent ? (
              <div className="flex items-center">
                <CheckCircleIcon className="mr-2 h-4 w-4 fill-green-400" />
                <span className="font-semibold text-sm text-green-600">{t('recovery.link_sent')}</span>
              </div>
            ) : null}
            {isOtpResent && error ? <p className="text-error text-sm">{t('recovery.link_send_error')}</p> : null}
          </div>
        </div>
      ) : null}
    </Layout>
  )
}
